import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    ChartBarIcon,
    ArrowTrendingUpIcon,
    TrophyIcon,
    SquaresPlusIcon, // Replaced 'TargetIcon' with 'SquaresPlusIcon'
    UsersIcon,
    CalendarIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';

// --- START: MOCK DATA ---
const mockPerformanceData = [
    {
        id: 1,
        name: 'Aisha Sharma',
        department: 'Engineering',
        performanceScore: 92,
        goalsCompleted: 7,
        lastReview: '2025-09-15',
    },
    {
        id: 2,
        name: 'Rahul Verma',
        department: 'Human Resources',
        performanceScore: 85,
        goalsCompleted: 5,
        lastReview: '2025-09-10',
    },
    {
        id: 3,
        name: 'Priya Singh',
        department: 'Marketing',
        performanceScore: 78,
        goalsCompleted: 4,
        lastReview: '2025-09-08',
    },
    {
        id: 4,
        name: 'Amit Patel',
        department: 'Sales',
        performanceScore: 95,
        goalsCompleted: 8,
        lastReview: '2025-09-12',
    },
    {
        id: 5,
        name: 'Neha Gupta',
        department: 'Engineering',
        performanceScore: 88,
        goalsCompleted: 6,
        lastReview: '2025-09-18',
    },
    {
        id: 6,
        name: 'Sandeep Reddy',
        department: 'Marketing',
        performanceScore: 75,
        goalsCompleted: 3,
        lastReview: '2025-09-05',
    },
    {
        id: 7,
        name: 'Alok Kumar',
        department: 'Sales',
        performanceScore: 91,
        goalsCompleted: 7,
        lastReview: '2025-09-14',
    },
];
// --- END: MOCK DATA ---

const GrowthPerformanceReport = () => {
    // Calculations for summary cards
    const totalEmployees = mockPerformanceData.length;
    const avgScore = (mockPerformanceData.reduce((sum, item) => sum + item.performanceScore, 0) / totalEmployees).toFixed(2);
    const topPerformers = mockPerformanceData.filter(item => item.performanceScore >= 90).length;
    const totalGoalsCompleted = mockPerformanceData.reduce((sum, item) => sum + item.goalsCompleted, 0);

    // Data for the simple bar chart
    const departmentScores = mockPerformanceData.reduce((acc, item) => {
        acc[item.department] = acc[item.department] || { totalScore: 0, count: 0 };
        acc[item.department].totalScore += item.performanceScore;
        acc[item.department].count += 1;
        return acc;
    }, {});
    const chartData = Object.keys(departmentScores).map(dept => ({
        name: dept,
        score: (departmentScores[dept].totalScore / departmentScores[dept].count).toFixed(2)
    }));
    const maxScore = 100;

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5, delay: 0.2 } },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-200 p-8 text-gray-800">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-7xl mx-auto"
            >
                <h1 className="text-4xl font-extrabold text-purple-800 flex items-center mb-4">
                    <ChartBarIcon className="h-10 w-10 text-purple-600 mr-4" />
                    Growth & Performance Report
                </h1>
                <p className="text-xl font-light text-purple-600 mb-8">
                    Analyze key metrics to drive employee growth and development.
                </p>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4"
                    >
                        <div className="p-3 bg-blue-100 rounded-full">
                            <ArrowTrendingUpIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-gray-500 font-semibold">Avg. Performance Score</p>
                            <h2 className="text-3xl font-bold text-gray-900">{avgScore} / 100</h2>
                        </div>
                    </motion.div>
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4"
                    >
                        <div className="p-3 bg-green-100 rounded-full">
                            <TrophyIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div>
                            <p className="text-gray-500 font-semibold">Top Performers</p>
                            <h2 className="text-3xl font-bold text-gray-900">{topPerformers}</h2>
                        </div>
                    </motion.div>
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4"
                    >
                        <div className="p-3 bg-red-100 rounded-full">
                            <SquaresPlusIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <div>
                            <p className="text-gray-500 font-semibold">Goals Completed</p>
                            <h2 className="text-3xl font-bold text-gray-900">{totalGoalsCompleted}</h2>
                        </div>
                    </motion.div>
                </div>

                {/* Performance Chart Section */}
                <div className="bg-white p-6 rounded-3xl shadow-lg mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <UsersIcon className="h-6 w-6 mr-2 text-gray-500" />
                        Average Score by Department
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {chartData.map((data, index) => (
                            <div key={index} className="flex items-center space-x-4">
                                <div className="w-24 text-sm font-semibold text-gray-600">{data.name}</div>
                                <div className="flex-1 bg-gray-200 rounded-full h-4">
                                    <div
                                        style={{ width: `${(data.score / maxScore) * 100}%` }}
                                        className="bg-purple-500 h-4 rounded-full transition-all duration-700"
                                    ></div>
                                </div>
                                <div className="w-12 text-sm font-semibold text-gray-800">{data.score}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Detailed Table */}
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance Score</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goals Completed</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Review</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {mockPerformanceData.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.department}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{item.performanceScore} / 100</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.goalsCompleted}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.lastReview}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default GrowthPerformanceReport;